import config from "../../../../../config.json";

function isChoiceRequired(choice) {
  const value = choice?.obligatory;
  return value === true || value === 1 || value === "true" || value === "1";
}

/** Pick the English detail, falling back to the first one with a value. */
function pickDetail(details, field) {
  const list = Array.isArray(details) ? details : [];
  const en = list.find((d) => d?.language === "en" && d?.[field]);
  if (en) return en[field];
  const first = list.find((d) => d?.[field]);
  return first ? first[field] : "";
}

export function categoryTitle(category, fallback = "No Title") {
  return pickDetail(category?.categoryDetails, "categoryTitle") || fallback;
}

export function subcategoryTitle(sub, fallback = "No Title") {
  return pickDetail(sub?.subCategoryDetails, "title") || fallback;
}

export function productTitle(product, fallback = "Unnamed Product") {
  return pickDetail(product?.productDetails, "productTitle") || fallback;
}

export function choiceTitle(choice, fallback = "Unnamed choice") {
  return pickDetail(choice?.choiceDetails, "choiceTitle") || fallback;
}

export function itemTitle(item, fallback = "Unnamed item") {
  return pickDetail(item?.itemDetails, "itemTitle") || fallback;
}

/** Product → choices → items, for the keep/hide picker. */
export function productChoiceChildren(product) {
  const hiddenMap = {};
  (product?.hiddenChoiceItems || []).forEach((row) => {
    const cid = normalizeRef(row?.choice);
    if (!cid) return;
    hiddenMap[cid] = (row.items || []).map((id) => String(id));
  });
  const choices = Array.isArray(product?.choices) ? product.choices : [];
  return choices
    .map((choice) => {
      if (!choice || typeof choice !== "object") return null;
      const id = normalizeRef(choice);
      if (!id || choice.isArchived === true) return null;
      const products = (Array.isArray(choice.items) ? choice.items : [])
        .filter((item) => item && item.isArchived !== true)
        .map((item) => {
          const itemId = normalizeRef(item);
          if (!itemId) return null;
          return {
            id: itemId,
            label: itemTitle(typeof item === "object" ? item : null),
          };
        })
        .filter(Boolean);
      const hiddenItemIds = hiddenMap[id] || [];
      return { id, label: choiceTitle(choice), products, hiddenItemIds };
    })
    .filter(Boolean);
}

export function menuTitle(menu, fallback = "Menu") {
  return pickDetail(menu?.menuDetails, "menuName") || menu?.menuName || fallback;
}

export function normalizeRef(ref) {
  if (ref == null) return null;
  if (typeof ref === "object" && ref._id != null) return String(ref._id);
  return String(ref);
}

export function mediaFileUrl(folder, photo) {
  if (!photo) return "";
  const raw = String(photo).trim().replace(/\\/g, "/");
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const filename = raw.includes("/") ? raw.split("/").filter(Boolean).pop() : raw;
  return filename ? `${config.UPLOADS_SERVER}/${folder}/${filename}` : "";
}

function visibleProductIdsFromList(list) {
  return (Array.isArray(list) ? list : [])
    .filter((row) => row?.product != null && row.product.isArchived !== true && !row.hiddenInMenu)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((row) => normalizeRef(row.product))
    .filter(Boolean);
}

function collectNestedProducts(categories) {
  const extra = {};
  (categories || []).forEach((category) => {
    (category?.productList || []).forEach((row) => {
      if (row?.product && typeof row.product === "object" && row.product._id) {
        extra[String(row.product._id)] = row.product;
      }
    });
    (category?.subcategories || []).forEach((entry) => {
      const sub = entry?.subCategory;
      (sub?.productList || []).forEach((row) => {
        if (row?.product && typeof row.product === "object" && row.product._id) {
          extra[String(row.product._id)] = row.product;
        }
      });
    });
  });
  return extra;
}

export function structureFromCategory(category) {
  return {
    productIds: visibleProductIdsFromList(category?.productList),
    subcategories: (Array.isArray(category?.subcategories) ? category.subcategories : [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((entry) => {
        const id = normalizeRef(entry.subCategory);
        if (!id) return null;
        const subObj = typeof entry.subCategory === "object" ? entry.subCategory : null;
        return {
          id,
          productIds: visibleProductIdsFromList(subObj?.productList),
          hideTitleFromClient:
            entry.hideTitleFromClient === true || subObj?.hideTitleFromClient === true,
        };
      })
      .filter(Boolean),
  };
}

function pushProduct(products, productPhotos, seenProducts, productId, productsById) {
  if (!productId || seenProducts.has(productId)) return;
  seenProducts.add(productId);
  const product = productsById[productId];
  if (!product) return;
  products.push({
    tempId: productId,
    price: product.price ?? 0,
    secondaryPrice: product.secondaryPrice,
    productDetails: product.productDetails,
    primaryLanguage: product.primaryLanguage,
  });
  if (product.imageDisabled) return;
  const url = mediaFileUrl(
    "product",
    Array.isArray(product.photos) ? product.photos[0] : product.photos
  );
  if (url) productPhotos[productId] = url;
}

/** Shape MenuTree data into the Start Now live-phone draft. */
export function buildManageMenuLivePreview({
  menu,
  restaurant,
  menuCategories,
  previewByCategory,
  productsById,
  subsById,
}) {
  const mergedProductsById = {
    ...collectNestedProducts(menuCategories),
    ...(productsById || {}),
  };
  const categoryProducts = {};
  const categorySubcategories = {};
  const subcategoryProducts = {};
  const menuCategoryIds = [];
  const categories = [];
  const subcategories = [];
  const products = [];
  const productPhotos = {};
  const seenProducts = new Set();
  const seenSubs = new Set();

  (menuCategories || []).forEach((category) => {
    if (!category?._id) return;
    const catId = String(category._id);
    menuCategoryIds.push(catId);
    categories.push({
      tempId: catId,
      primaryLanguage: category.primaryLanguage,
      categoryDetails: category.categoryDetails,
      hideTitleFromClient:
        category.menuHideCategoryTitle === true || category.hideTitleFromClient === true,
    });

    const live = previewByCategory?.[catId] || structureFromCategory(category);
    const productIds = live.productIds || [];
    const liveSubs = live.subcategories || [];
    categoryProducts[catId] = productIds;
    categorySubcategories[catId] = liveSubs.map((sub) => sub.id);
    productIds.forEach((pid) =>
      pushProduct(products, productPhotos, seenProducts, pid, mergedProductsById)
    );

    liveSubs.forEach((sub) => {
      subcategoryProducts[sub.id] = sub.productIds || [];
      if (!seenSubs.has(sub.id)) {
        seenSubs.add(sub.id);
        const full = subsById?.[sub.id];
        subcategories.push({
          tempId: sub.id,
          primaryLanguage: full?.primaryLanguage,
          subCategoryDetails: full?.subCategoryDetails,
          hideTitleFromClient: sub.hideTitleFromClient === true,
          displayOrientation:
            full?.displayOrientation === "horizontal" ? "horizontal" : "vertical",
          bannerImage: full?.bannerImage || null,
          bannerImageDisabled: full?.bannerImageDisabled === true,
          bannerUrl:
            full?.bannerImageDisabled === true
              ? null
              : mediaFileUrl("category", full?.bannerImage) || null,
        });
      }
      (sub.productIds || []).forEach((pid) =>
        pushProduct(products, productPhotos, seenProducts, pid, mergedProductsById)
      );
    });
  });

  return {
    draft: {
      visualSettingsMode: "custom",
      menu: {
        primaryLanguage: menu?.primaryLanguage || "en",
        optionalLanguages: menu?.optionalLanguages || [],
        menuDetails: menu?.menuDetails || [],
        productImagesDisabled: Boolean(menu?.productImagesDisabled),
        productPricesHidden: Boolean(menu?.productPricesHidden),
        searchBarHidden: Boolean(menu?.searchBarHidden),
      },
      restaurant: {
        clientMenuShowLogo: restaurant?.clientMenuShowLogo !== false,
        clientShowAllMenusOnOnePage: restaurant?.clientShowAllMenusOnOnePage === true,
      },
      products,
      categories,
      subcategories,
      relations: {
        categoryProducts,
        categorySubcategories,
        subcategoryProducts,
        menuCategories: menuCategoryIds,
      },
    },
    productPhotos,
    menuPhotoPreview: mediaFileUrl("menu", menu?.photo) || null,
  };
}

/** Shape add/edit subcategory form data into the Start Now live-phone draft. */
export function buildSubcategoryFormLivePreview({
  titles = {},
  productsById = {},
  selectedProductIds = [],
  displayOrientation = "vertical",
  bannerUrl = null,
  bannerImageDisabled = false,
}) {
  const CAT_ID = "preview-category";
  const SUB_ID = "preview-subcategory";
  const codes = Object.keys(titles);
  const filledTitle = (code) => String(titles[code] || "").trim();
  const previewLang = codes.find((code) => filledTitle(code)) || "en";
  const subCategoryDetails = codes
    .map((code) => ({
      language: code,
      title: filledTitle(code),
      description: "",
    }))
    .filter((detail) => detail.title);

  const products = [];
  const productPhotos = {};
  (selectedProductIds || []).forEach((productId) => {
    const product = productsById[productId];
    if (!product) return;
    products.push({
      tempId: productId,
      price: product.price ?? 0,
      secondaryPrice: product.secondaryPrice,
      productDetails: product.productDetails,
      primaryLanguage: product.primaryLanguage,
    });
    if (product.imageDisabled) return;
    const url = mediaFileUrl(
      "product",
      Array.isArray(product.photos) ? product.photos[0] : product.photos
    );
    if (url) productPhotos[productId] = url;
  });

  const showBanner = Boolean(bannerUrl) && bannerImageDisabled !== true;

  return {
    draft: {
      visualSettingsMode: "custom",
      menu: {
        primaryLanguage: previewLang,
        menuDetails: [{ language: previewLang, menuName: "" }],
        productImagesDisabled: false,
        productPricesHidden: false,
        searchBarHidden: false,
      },
      restaurant: {
        clientMenuShowLogo: true,
        clientShowAllMenusOnOnePage: false,
      },
      products,
      categories: [
        {
          tempId: CAT_ID,
          primaryLanguage: previewLang,
          categoryDetails: [],
        },
      ],
      subcategories: [
        {
          tempId: SUB_ID,
          primaryLanguage: previewLang,
          subCategoryDetails,
          displayOrientation:
            displayOrientation === "horizontal" ? "horizontal" : "vertical",
          bannerImageDisabled: bannerImageDisabled === true,
          bannerUrl: showBanner ? bannerUrl : null,
        },
      ],
      relations: {
        menuCategories: [CAT_ID],
        categoryProducts: { [CAT_ID]: [] },
        categorySubcategories: { [CAT_ID]: [SUB_ID] },
        subcategoryProducts: { [SUB_ID]: selectedProductIds || [] },
      },
    },
    productPhotos,
  };
}

/** Shape add/edit product form data into the Start Now live-phone draft. */
export function buildProductFormLivePreview({
  productDetails = {},
  price = 0,
  secondaryPrice,
  photoUrl = null,
  imageDisabled = false,
}) {
  const PRODUCT_ID = "preview-product";
  const CAT_ID = "preview-category";
  const SUB_ID = "preview-subcategory";
  const codes = Object.keys(productDetails);
  const filledTitle = (code) => String(productDetails[code]?.productTitle || "").trim();
  const previewLang = codes.find((code) => filledTitle(code)) || "en";
  const previewTitle = filledTitle(previewLang);
  const details = codes
    .map((code) => ({
      language: code,
      productTitle: filledTitle(code),
      productDescription: String(productDetails[code]?.productDescription || "").trim(),
    }))
    .filter((entry) => entry.productTitle || entry.productDescription);

  const hasPhoto = Boolean(!imageDisabled && photoUrl);
  const hasDescription = details.some((entry) => entry.productDescription);
  const hasPreview = Boolean(previewTitle || hasPhoto || hasDescription);
  const productPhotos = {};
  if (hasPhoto) productPhotos[PRODUCT_ID] = photoUrl;

  return {
    draft: {
      visualSettingsMode: "custom",
      menu: {
        primaryLanguage: previewLang,
        menuDetails: [{ language: previewLang, menuName: "" }],
        productImagesDisabled: false,
        productPricesHidden: false,
        searchBarHidden: false,
      },
      restaurant: {
        clientMenuShowLogo: true,
        clientShowAllMenusOnOnePage: false,
      },
      products: hasPreview
        ? [
            {
              tempId: PRODUCT_ID,
              price: price ?? 0,
              secondaryPrice,
              productDetails: details,
              primaryLanguage: previewLang,
            },
          ]
        : [],
      categories: [
        {
          tempId: CAT_ID,
          primaryLanguage: previewLang,
          categoryDetails: [],
        },
      ],
      subcategories: [
        {
          tempId: SUB_ID,
          primaryLanguage: previewLang,
          subCategoryDetails: [],
        },
      ],
      relations: {
        menuCategories: [CAT_ID],
        categoryProducts: { [CAT_ID]: [] },
        categorySubcategories: { [CAT_ID]: [SUB_ID] },
        subcategoryProducts: { [SUB_ID]: hasPreview ? [PRODUCT_ID] : [] },
      },
    },
    productPhotos,
  };
}

/** Product-page preview used on create-product step 2 (choices + items). */
export function buildProductDetailLivePreview({
  productDetails = {},
  price = 0,
  secondaryPrice,
  photoUrl = null,
  imageDisabled = false,
  selectedChoiceIds = [],
  choices = [],
  choiceItemsMap = {},
  selectedItemsByChoice = {},
}) {
  const details = Object.keys(productDetails).map((code) => ({
    language: code,
    productTitle: String(productDetails[code]?.productTitle || "").trim(),
    productDescription: String(productDetails[code]?.productDescription || "").trim(),
  }));
  const byId = new Map((choices || []).map((choice) => [String(choice._id), choice]));

  return {
    title: pickDetail(details, "productTitle"),
    description: pickDetail(details, "productDescription"),
    price: price ?? 0,
    secondaryPrice,
    photoUrl: imageDisabled ? null : photoUrl || null,
    choices: (selectedChoiceIds || [])
      .map((choiceId) => {
        const choice = byId.get(String(choiceId));
        if (!choice) return null;
        const allItems =
          choiceItemsMap[choiceId] || choiceItemsMap[String(choiceId)] || [];
        const kept =
          selectedItemsByChoice[choiceId] ?? selectedItemsByChoice[String(choiceId)];
        const keptIds = new Set(
          (kept || allItems.map((item) => item._id)).map(String)
        );
        return {
          id: String(choice._id),
          title: choiceTitle(choice, ""),
          obligatory: isChoiceRequired(choice),
          minSelected: Number(choice.minSelected) || 0,
          maxSelected: Number(choice.maxSelected) || 0,
          items: allItems
            .filter((item) => keptIds.has(String(item._id)))
            .map((item) => ({
              id: String(item._id),
              title: itemTitle(item, ""),
              price: item?.price ?? 0,
              havePrice: item?.havePrice !== false,
              secondaryPrice: item?.secondaryPrice,
            })),
        };
      })
      .filter(Boolean),
  };
}

/** Product-page preview used on add/edit choice. Product name stays untitled. */
export function buildChoiceFormLivePreview({
  titles = {},
  minSelected = 0,
  maxSelected = 1,
  obligatory = false,
  items = [],
}) {
  const details = Object.keys(titles).map((code) => ({
    language: code,
    choiceTitle: String(titles[code] || "").trim(),
  }));
  const previewItems = (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .map((item, index) => ({
      id: String(item.id || item._id || `preview-item-${index}`),
      title: item.title || itemTitle(item, ""),
      price: item.price ?? 0,
      havePrice: item.havePrice !== false,
      secondaryPrice: item.secondaryPrice,
    }));
  return {
    title: "",
    description: "",
    price: 0,
    hidePrice: true,
    photoUrl: null,
    choices: [
      {
        id: "preview-choice",
        title: pickDetail(details, "choiceTitle"),
        obligatory: isChoiceRequired({ obligatory }),
        minSelected: Number(minSelected) || 0,
        maxSelected: Number(maxSelected) || 0,
        items: previewItems,
      },
    ],
  };
}

/** Product-page preview used on add/edit item. Product name stays untitled. */
export function buildItemFormLivePreview({
  titles = {},
  price = 0,
  havePrice = true,
  secondaryPrice,
}) {
  const details = Object.keys(titles).map((code) => ({
    language: code,
    itemTitle: String(titles[code] || "").trim(),
  }));
  return {
    title: "",
    description: "",
    price: 0,
    hidePrice: true,
    photoUrl: null,
    choices: [
      {
        id: "preview-choice",
        title: "",
        obligatory: false,
        minSelected: 0,
        maxSelected: 1,
        items: [
          {
            id: "preview-item",
            title: pickDetail(details, "itemTitle"),
            price: havePrice ? Number(price || 0) : 0,
            havePrice: havePrice !== false,
            secondaryPrice: havePrice ? secondaryPrice : undefined,
          },
        ],
      },
    ],
  };
}
